<template>
  <div>
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6">
      <div class="max-w-md">
        <h2 class="text-2xl font-medium text-sunset-dark mb-2">Your Remote Work Bingo</h2>
        <p class="subtitle">Check off each square as you experience these remote work situations in your digital nomad life</p>
      </div>
      <div class="flex flex-wrap gap-3">
        <button @click="regenerateCard" class="btn-secondary flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Shuffle Card
        </button>
        <button @click="shareCard" class="btn-secondary flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share Card
        </button>
        <button @click="exportCard" class="btn flex items-center gap-2" :disabled="isExporting">
          <svg v-if="!isExporting" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          <svg v-else class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ isExporting ? 'Creating Image...' : 'Export Card' }}
        </button>
      </div>
    </div>

    <div class="card" ref="bingoCardRef">
      <BingoCard :squares="bingoSquares" @update-square="updateSquare" />
    </div>
    
    <div class="mt-6 text-sm text-center">
      <p v-if="exportSuccess" class="text-sunset-orange font-medium flex items-center justify-center gap-2 animate-pulse">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Beautiful bingo card saved to your downloads!
      </p>
      <p v-else class="text-sunset-dark text-opacity-60">
        Click squares to mark them as completed. Your bingo card and progress are saved automatically. Use the Share button to share with friends!
      </p>
    </div>
  </div>
</template>

<script>
import BingoCard from '@/components/BingoCard.vue'
import html2canvas from 'html2canvas'
import { bingoData } from '@/utils/bingoData'

export default {
  name: 'HomeView',
  components: {
    BingoCard
  },
  data() {
    return {
      bingoSquares: [],
      cardId: null,
      isExporting: false,
      exportSuccess: false,
    }
  },
  created() {
    const fromSharedUrl = this.loadStateFromUrl()
    
    if (fromSharedUrl) {
      // If loaded from shared URL, save to localStorage and clean URL
      this.saveCardToLocalStorage()
      this.cleanUrl()
    } else {
      // Otherwise, try to load from localStorage or generate new
      this.loadCardFromLocalStorage() || this.generateNewCard()
    }
    
    // Load checked state
    this.loadCheckedState()
  },
  methods: {
    generateNewCard() {
      // Generate a unique card ID for storing checked state in localStorage
      this.cardId = Date.now().toString(36) + Math.random().toString(36).substring(2, 9)
      
      // Shuffle array and pick 25 items for a full bingo card
      const shuffled = [...bingoData].sort(() => 0.5 - Math.random())
      const selected = shuffled.slice(0, 25)
      
      // Create a 5x5 grid
      this.bingoSquares = []
      
      for (let i = 0; i < 25; i++) {
        const item = {
          id: selected[i].id,
          text: selected[i].text,
          isChecked: false,
          isFreeSpace: false
        }
        
        this.bingoSquares.push(item)
      }
      
      // Save to localStorage instead of URL by default
      this.saveCardToLocalStorage()
      this.saveCheckedState()
      return true
    },
    saveCardToLocalStorage() {
      // Save card layout to localStorage
      const cardData = {
        cardId: this.cardId,
        squares: this.bingoSquares.map(square => ({
          id: square.id
        }))
      }
      
      localStorage.setItem('remote-bingo-current-card', JSON.stringify(cardData))
    },
    loadCardFromLocalStorage() {
      // Try to load card from localStorage
      const savedCard = localStorage.getItem('remote-bingo-current-card')
      
      if (!savedCard) return false
      
      try {
        const cardData = JSON.parse(savedCard)
        
        if (cardData.cardId && cardData.squares && cardData.squares.length === 25) {
          // Set card ID
          this.cardId = cardData.cardId
          
          // Reconstruct the bingo squares
          this.bingoSquares = cardData.squares.map(square => {
            return {
              id: square.id,
              text: this.findTextById(square.id),
              isChecked: false, // Initialize unchecked, will be updated from localStorage
              isFreeSpace: false
            }
          })
          
          return true
        }
      } catch (e) {
        console.error('Failed to parse saved card from localStorage', e)
      }
      
      return false
    },
    cleanUrl() {
      // Remove card parameter from URL
      window.history.replaceState(null, '', window.location.pathname + window.location.hash.split('?')[0])
    },
    regenerateCard() {
      this.generateNewCard()
    },
    updateSquare(index) {
      this.bingoSquares[index].isChecked = !this.bingoSquares[index].isChecked
      this.saveCheckedState()
      // No URL update needed as we store state only in localStorage
    },
    updateUrlState() {
      // Create a serialized state of the bingo card layout ONLY (not checked state)
      const state = {
        cardId: this.cardId,
        squares: this.bingoSquares.map(square => ({
          id: square.id
        }))
      }
      
      // Update URL with the stringified state
      const queryParams = new URLSearchParams()
      queryParams.set('card', JSON.stringify(state))
      
      // Replace current URL state
      const newUrl = window.location.pathname + '#/?' + queryParams.toString()
      window.history.replaceState(null, '', newUrl)
    },
    loadStateFromUrl() {
      const queryParams = new URLSearchParams(window.location.hash.split('?')[1] || '')
      const cardStr = queryParams.get('card')
      
      if (!cardStr) return false
      
      try {
        const state = JSON.parse(cardStr)
        
        if (state.squares && state.squares.length === 25) {
          // Set card ID from URL
          this.cardId = state.cardId || Date.now().toString(36) + Math.random().toString(36).substring(2, 9)
          
          // Reconstruct the bingo squares from the URL state
          // eslint-disable-next-line no-unused-vars
          this.bingoSquares = state.squares.map((square, index) => {
            return {
              id: square.id,
              text: this.findTextById(square.id),
              isChecked: false, // Initialize unchecked, will be updated from localStorage
              isFreeSpace: false
            }
          })
          
          return true
        }
      } catch (e) {
        console.error('Failed to parse card from URL', e)
      }
      
      return false
    },
    saveCheckedState() {
      if (!this.cardId) return
      
      // Get checked states only
      const checkedState = this.bingoSquares.map((square, index) => ({ 
        index, 
        checked: square.isChecked 
      })).filter(square => square.checked)
      
      // Save to localStorage
      localStorage.setItem(`bingo-card-${this.cardId}`, JSON.stringify(checkedState))
    },
    loadCheckedState() {
      if (!this.cardId) return
      
      // Load from localStorage
      const storageKey = `bingo-card-${this.cardId}`
      const savedState = localStorage.getItem(storageKey)
      
      if (savedState) {
        try {
          const checkedSquares = JSON.parse(savedState)
          
          // Apply checked state to squares
          checkedSquares.forEach(item => {
            if (this.bingoSquares[item.index]) {
              this.bingoSquares[item.index].isChecked = true
            }
          })
        } catch (e) {
          console.error('Failed to parse saved state from localStorage', e)
        }
      }
    },
    findTextById(id) {
      const item = bingoData.find(item => item.id === id)
      return item ? item.text : 'Unknown'
    },
    shareCard() {
      // For sharing, we need to put the card state in the URL temporarily
      this.updateUrlState()
      
      // Get the current URL to share
      const shareUrl = window.location.href
      
      // Check if Navigator Share API is available
      if (navigator.share) {
        navigator.share({
          title: 'Remote Developer Bingo',
          text: 'Check out my Remote Developer Bingo card!',
          url: shareUrl
        }).catch(error => {
          console.error('Error sharing:', error)
          this.copyToClipboard(shareUrl)
        }).finally(() => {
          // Clean URL after sharing
          setTimeout(() => this.cleanUrl(), 1000)
        })
      } else {
        // Fallback to clipboard
        this.copyToClipboard(shareUrl)
        
        // Clean URL after copying
        setTimeout(() => this.cleanUrl(), 1000)
      }
    },
    
    copyToClipboard(text) {
      // Create temporary element
      const el = document.createElement('textarea')
      el.value = text
      document.body.appendChild(el)
      el.select()
      
      try {
        // Copy text
        document.execCommand('copy')
        alert('Bingo card URL copied to clipboard!')
      } catch (err) {
        console.error('Failed to copy URL to clipboard:', err)
        alert('Failed to copy URL. Please copy it manually: ' + text)
      } finally {
        document.body.removeChild(el)
      }
    },
    
    async exportCard() {
      if (this.isExporting) return
      
      this.isExporting = true
      try {
        // Create a temporary export container
        const exportContainer = document.createElement('div')
        exportContainer.className = 'export-container'
        exportContainer.style.width = '800px'
        exportContainer.style.padding = '40px'
        exportContainer.style.background = 'linear-gradient(120deg, #FF7E5F 0%, #FEB47B 100%)'
        exportContainer.style.borderRadius = '16px'
        exportContainer.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.1)'
        exportContainer.style.position = 'fixed'
        exportContainer.style.left = '-9999px' // Hide off-screen
        exportContainer.style.top = '-9999px'
        document.body.appendChild(exportContainer)
        
        // Create header
        const header = document.createElement('div')
        header.style.marginBottom = '20px'
        header.style.textAlign = 'center'
        header.style.color = 'white'
        
        const title = document.createElement('h1')
        title.textContent = 'Remote Developer Bingo'
        title.style.fontSize = '32px'
        title.style.fontWeight = 'bold'
        title.style.marginBottom = '8px'
        title.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        
        const subtitle = document.createElement('p')
        subtitle.textContent = 'The Digital Nomad Lifestyle'
        subtitle.style.fontSize = '18px'
        subtitle.style.opacity = '0.9'
        subtitle.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        
        header.appendChild(title)
        header.appendChild(subtitle)
        exportContainer.appendChild(header)
        
        // Clone the bingo card and style it
        const cardClone = this.$refs.bingoCardRef.cloneNode(true)
        cardClone.style.borderRadius = '12px'
        cardClone.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'
        cardClone.style.padding = '20px'
        cardClone.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.1)'
        exportContainer.appendChild(cardClone)
        
        // Add footer
        const footer = document.createElement('div')
        footer.style.marginTop = '20px'
        footer.style.textAlign = 'center'
        footer.style.color = 'white'
        footer.style.fontSize = '14px'
        footer.style.opacity = '0.8'
        footer.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        
        // Calculate bingo status for the footer text
        let checkedCount = 0
        for (const square of this.bingoSquares) {
          if (square.isChecked) {
            checkedCount++
          }
        }
        
        const percentage = Math.round((checkedCount / 25) * 100)
        const footerText = document.createElement('p')
        footerText.textContent = `${checkedCount}/25 items completed (${percentage}%) • Generated at ${new Date().toLocaleString()}`
        
        footer.appendChild(footerText)
        exportContainer.appendChild(footer)
        
        // Create watermark
        const watermark = document.createElement('div')
        watermark.style.position = 'absolute'
        watermark.style.bottom = '15px'
        watermark.style.right = '20px'
        watermark.style.fontSize = '12px'
        watermark.style.opacity = '0.7'
        watermark.style.color = 'white'
        watermark.textContent = 'Remote Developer Bingo'
        watermark.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        exportContainer.appendChild(watermark)
        
        // Use html2canvas to capture the styled container
        const canvas = await html2canvas(exportContainer, {
          scale: 2, // Higher resolution
          useCORS: true,
          allowTaint: true,
          backgroundColor: null
        })
        
        // Clean up - remove the temporary container
        document.body.removeChild(exportContainer)
        
        // Convert to JPEG and trigger download
        const jpegUrl = canvas.toDataURL('image/jpeg', 0.9)
        const link = document.createElement('a')
        link.href = jpegUrl
        link.download = 'remote-dev-bingo.jpg'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Show success message
        this.exportSuccess = true
        setTimeout(() => {
          this.exportSuccess = false
        }, 5000) // Hide after 5 seconds
      } catch (error) {
        console.error('Error exporting card:', error)
        alert('Sorry, there was an error creating your export. Please try again.')
      } finally {
        this.isExporting = false
      }
    }
  }
}
</script>